/**
 * Ultra-Modern Sidebar Component
 * World-Class SaaS ERP Platform Design
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  EUROASIANN_GROUP_NAME,
  EUROASIANN_LOGO_DARK,
  EUROASIANN_LOGO_LIGHT,
  EUROASIANN_TAGLINE,
} from '../../constants/euroasiann-branding';
import {
  MdDashboard,
  MdBusinessCenter,
  MdVpnKey,
  MdRocketLaunch,
  MdLogout,
  MdPeople,
  MdSettings,
  MdAdminPanelSettings,
  MdBarChart,
  MdAssignment,
  MdInventory,
  MdStore,
  MdCategory,
  MdModelTraining,
  MdFactCheck,
  MdPeopleOutline,
  MdExpandMore,
  MdExpandLess,
  MdSupport,
  MdChat,
  MdBugReport,
  MdWarning,
  MdLocationOn,
  MdDirectionsBoat,
  MdStorage,
  MdBuild,
  MdShoppingCart,
  MdLocalShipping,
  MdReportProblem,
  MdInsights,
  MdTrendingUp,
  MdShoppingBag,
  MdContactMail,
} from 'react-icons/md';
import { IconType } from 'react-icons';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { getAdminVesselAccess } from '../../utils/vesselPermissions';
import { SidebarToggleIcon } from '../SidebarToggleIcon';

interface NavItem {
  path?: string;
  label: string;
  icon: IconType;
  badge?: string;
  permission?: string;
  children?: NavItem[];
}

/* ============================================================
   ⭐ NAV ITEMS WITH PARTS HIERARCHY ADDED
   ============================================================ */
const navItems: NavItem[] = [
  {
    label: 'Admin Dashboard',
    icon: MdDashboard,
    children: [
      { path: '/dashboard/admin', label: 'Dashboard', icon: MdDashboard },
      { path: '/dashboard/admin/shipments', label: 'Shipments', icon: MdLocalShipping },
      { path: '/dashboard/admin/inventory', label: 'Inventory', icon: MdInventory },
      {
        path: '/dashboard/admin/product-hierarchy',
        label: 'Parts Hierarchy',
        icon: MdCategory,
      },
      {
        path: '/dashboard/admin/partfinder-intelligence',
        label: 'PartFinder Intelligence',
        icon: MdInsights,
      },
      {
        path: '/dashboard/admin/oem-pms',
        label: 'OEM PMS Library',
        icon: MdBuild,
      },
    ],
  },
  { path: '/dashboard/admin/sales', label: 'Sales', icon: MdTrendingUp },
  { path: '/dashboard/admin/purchases', label: 'Purchases', icon: MdShoppingBag },
  { path: '/users', label: 'Users', icon: MdPeople },

  {
    label: 'Customers',
    icon: MdPeople,
    children: [
      { path: '/dashboard/admin/customers/support', label: 'Support', icon: MdSupport },
    ],
  },
  { path: '/organizations', label: 'Organizations', icon: MdBusinessCenter },
  { path: '/crm', label: 'CRM', icon: MdContactMail },
  { path: '/agreements', label: 'Agreements', icon: MdAssignment },
  {
    label: 'Database Management',
    icon: MdStorage,
    children: [
      { path: '/vessel-database', label: 'Vessel Management', icon: MdDirectionsBoat, permission: 'adminVesselsView' },
      { path: '/port-management', label: 'Port Management', icon: MdLocationOn },
      { path: '/dashboard/admin/classification-societies', label: 'Classification Societies', icon: MdFactCheck },
      { path: '/dashboard/admin/classification-surveyors', label: 'Classification Surveyors', icon: MdPeopleOutline },
      { path: '/data-management', label: 'Data Seeding', icon: MdStorage },
    ],
  },
  {
    label: 'Licenses',
    icon: MdVpnKey,
    children: [
      { path: '/licenses', label: 'All Licenses', icon: MdVpnKey },
      { path: '/licenses/upgrade-requests', label: 'Upgrade Requests', icon: MdAssignment },
    ],
  },
  {
    label: 'Risk & Incidents',
    icon: MdWarning,
    children: [
      { path: '/dashboard/admin/risks', label: 'Risk Management', icon: MdWarning },
    ],
  },

  {
    label: 'Role Management',
    icon: MdVpnKey,
    children: [
      { path: '/dashboard/admin/roles', label: 'Roles & Permissions', icon: MdSettings },
      { path: '/dashboard/admin/assign-roles', label: 'Assign Roles', icon: MdPeople },
    ],
  },

  {
    label: 'Support',
    icon: MdSupport,
    children: [
      { path: '/support/tickets', label: 'Support Tickets', icon: MdSupport },
      { path: '/support/chat', label: 'Customer Support', icon: MdChat },
    ],
  },

  { path: '/analytics', label: 'Analytics', icon: MdBarChart },
  { path: '/settings', label: 'Settings', icon: MdSettings },
];

/**
 * True when `path` is the single best (longest) match for `pathname`
 * among all known nav paths — so only one leaf item highlights at a time.
 */
function isBestPathMatch(pathname: string, path: string, allPaths: string[] = []): boolean {
  if (!path) return false;
  const matches = pathname === path || pathname.startsWith(`${path}/`);
  if (!matches) return false;

  // Prefer any longer path that also matches the current location
  return !allPaths.some(
    (other) =>
      other !== path &&
      other.length > path.length &&
      (pathname === other || pathname.startsWith(`${other}/`))
  );
}

/** Collect every navigable path from the sidebar tree for exclusive active matching. */
function collectNavPaths(items: NavItem[]): string[] {
  const paths: string[] = [];
  for (const item of items) {
    if (item.path) paths.push(item.path);
    if (item.children?.length) paths.push(...collectNavPaths(item.children));
  }
  return paths;
}

const ALL_NAV_PATHS = collectNavPaths(navItems);

/* ============================================================
   ⭐ SIDEBAR COMPONENT
   ============================================================ */
interface SidebarProps {
  collapsed: boolean;
  onToggle: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const { user, logout, permissions } = useAuth();
  const { resolvedTheme } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const brandLogoSrc = resolvedTheme === 'dark' ? EUROASIANN_LOGO_DARK : EUROASIANN_LOGO_LIGHT;

  const [tooltip, setTooltip] = useState<{ label: string; x: number; y: number } | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [brandHovered, setBrandHovered] = useState(false);
  const tooltipTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!collapsed) setBrandHovered(false);
  }, [collapsed]);


  const vesselAccess = getAdminVesselAccess(user?.role, permissions ?? []);
  const canAccessNavItem = (item: NavItem) => {
    if (!item.permission) return true;
    if (item.permission === 'adminVesselsView') return vesselAccess.canView;
    return (permissions ?? []).includes(item.permission);
  };

  // undefined = follow active route; null = force closed; string = manually opened section
  const [manualOpenSection, setManualOpenSection] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    // Leaving a subsection closes unrelated dropdowns (route becomes source of truth again)
    setManualOpenSection(undefined);
  }, [location.pathname]);

  const activeSectionLabel =
    navItems.find((item) =>
      item.children?.some((child) =>
        isBestPathMatch(location.pathname, child.path || '', ALL_NAV_PATHS),
      ),
    )?.label ?? null;

  const openSectionLabel =
    manualOpenSection === undefined ? activeSectionLabel : manualOpenSection;

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      navigate('/login');
    }
  };

  const handleMouseEnter = (e: React.MouseEvent, label: string) => {
    if (collapsed) {
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
      setTooltip({ label, x: e.clientX, y: e.clientY });
      setShowTooltip(false);
      tooltipTimeoutRef.current = setTimeout(() => setShowTooltip(true), 150);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (collapsed && tooltip) {
      setTooltip({ ...tooltip, x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseLeave = () => {
    if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    setShowTooltip(false);
    setTooltip(null);
  };

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
    };
  }, []);

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-50 flex h-screen flex-col overflow-hidden bg-[hsl(var(--background))] border-r border-[hsl(var(--border))] transition-all duration-300',
        collapsed ? 'w-20' : 'w-72'
      )}
    >
      {/* Logo Section — h-16 matches top header so border-b / border-r meet cleanly */}
      <div
        className={cn(
          'app-sidebar-brand flex h-16 shrink-0 items-center overflow-hidden border-b border-[hsl(var(--border))] bg-[hsl(var(--background))]',
          collapsed ? 'justify-center px-2' : 'justify-between px-4'
        )}
      >
        {collapsed ? (
          <button
            type="button"
            onMouseEnter={() => setBrandHovered(true)}
            onMouseLeave={() => setBrandHovered(false)}
            onFocus={() => setBrandHovered(true)}
            onBlur={() => setBrandHovered(false)}
            onClick={() => onToggle(false)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0088cc]/40"
            aria-label="Open sidebar"
            title="Open sidebar"
          >
            {brandHovered ? (
              <SidebarToggleIcon className="h-5 w-5" />
            ) : (
              <img
                src={brandLogoSrc}
                alt={EUROASIANN_GROUP_NAME}
                className="h-full w-full object-contain"
              />
            )}
          </button>
        ) : (
          <>
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                <img
                  src={brandLogoSrc}
                  alt={EUROASIANN_GROUP_NAME}
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-sm font-bold leading-tight text-[hsl(var(--foreground))]">
                  {EUROASIANN_GROUP_NAME}
                </h1>
                <p className="truncate text-[10px] capitalize text-[hsl(var(--muted-foreground))]">
                  {EUROASIANN_TAGLINE}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => onToggle(true)}
              className="shrink-0 rounded-lg p-1.5 text-[hsl(var(--muted-foreground))] transition-colors hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0088cc]/40"
              aria-label="Close sidebar"
              title="Close sidebar"
            >
              <SidebarToggleIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className={cn('flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-1', collapsed ? 'px-2' : 'px-3')}>
        {navItems.map((item) => {
          const visibleChildren = item.children?.filter(canAccessNavItem);
          if (item.children && (!visibleChildren || visibleChildren.length === 0)) {
            return null;
          }
          if (!item.children && !canAccessNavItem(item)) {
            return null;
          }
          const Icon = item.icon;
          const children = visibleChildren || item.children;
          const hasChildren = children && children.length > 0;
          const hasActiveChild = Boolean(
            children?.some((child) =>
              isBestPathMatch(location.pathname, child.path || '', ALL_NAV_PATHS)
            )
          );
          const isExpanded = openSectionLabel === item.label;
          const isActive = item.path
            ? isBestPathMatch(location.pathname, item.path, ALL_NAV_PATHS)
            : false;

          if (hasChildren) {
            return (
              <div key={item.label}>
                <button
                  onClick={() => {
                    if (!collapsed) {
                      // Accordion: only one module dropdown open at a time
                      setManualOpenSection(isExpanded ? null : item.label);
                    }
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, item.label)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  className={cn(
                    'group relative flex items-center gap-3 py-2.5 rounded-lg transition-all duration-200 w-full text-left',
                    collapsed ? 'justify-center px-2' : 'px-3',
                    hasActiveChild
                      ? 'bg-[#0088cc] text-white font-semibold'
                      : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                  )}
                >
                  <Icon className={cn('w-5 h-5 flex-shrink-0', hasActiveChild && 'text-white')} />
                  {!collapsed && (
                    <>
                      <span
                        data-sidebar-nav-label="module"
                        className="flex-1 text-[12px] font-semibold uppercase tracking-wider"
                        style={{ fontSize: '12px' }}
                      >
                        {t(item.label)}
                      </span>
                      {isExpanded ? <MdExpandLess className="w-4 h-4" /> : <MdExpandMore className="w-4 h-4" />}
                    </>
                  )}
                </button>

                {/* Child Items */}
                {!collapsed && isExpanded && children && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-[hsl(var(--border))] pl-2">
                    {children.map((child) => {
                      const ChildIcon = child.icon;
                      const isChildActive = isBestPathMatch(
                        location.pathname,
                        child.path || '',
                        ALL_NAV_PATHS
                      );

                      return (
                        <a
                          key={child.path}
                          href={child.path!}
                          onClick={(e) => {
                            e.preventDefault();
                            if (child.path && location.pathname !== child.path) {
                              navigate(child.path);
                            } else {
                              setManualOpenSection(undefined);
                            }
                          }}
                          onMouseEnter={(e) => handleMouseEnter(e, child.label)}
                          onMouseMove={handleMouseMove}
                          onMouseLeave={handleMouseLeave}
                          className={cn(
                            'group relative flex items-center gap-3 py-2 rounded-lg transition-all duration-200 px-3',
                            isChildActive
                              ? 'bg-[#0088cc]/20 text-[#0088cc] dark:text-blue-400 font-semibold'
                              : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                          )}
                        >
                          <ChildIcon
                            className={cn(
                              'w-4 h-4 flex-shrink-0',
                              isChildActive && 'text-[#0088cc] dark:text-blue-400'
                            )}
                          />
                          <span
                            data-sidebar-nav-label="sub"
                            className="flex-1 text-[11px] font-semibold uppercase tracking-wider flex items-center justify-between"
                            style={{ fontSize: '11px' }}
                          >
                            {t(child.label)}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // Single-level items
          return (
            <a
              key={item.path}
              href={item.path!}
              onClick={(e) => {
                e.preventDefault();
                if (item.path && location.pathname !== item.path) {
                  navigate(item.path);
                }
              }}
              onMouseEnter={(e) => handleMouseEnter(e, item.label)}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className={cn(
                'group relative flex items-center gap-3 py-2.5 rounded-lg transition-all duration-200',
                collapsed ? 'justify-center px-2' : 'px-3',
                isActive
                  ? 'bg-[#0088cc] text-white font-semibold'
                  : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
              )}
            >
              <Icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-white')} />
              {!collapsed && (
                <>
                  <span
                    data-sidebar-nav-label="module"
                    className="flex-1 text-[12px] font-semibold uppercase tracking-wider"
                    style={{ fontSize: '12px' }}
                  >
                    {t(item.label)}
                  </span>
                  {item.badge && (
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-[#0088cc]/20 text-[#0088cc] dark:text-blue-400">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </a>
          );
        })}
      </nav>

      {/* Logout Section */}
      <div className={cn('border-t border-[hsl(var(--border))] bg-[hsl(var(--background))] overflow-hidden', collapsed ? 'px-2 py-3' : 'p-4')}>
        <button
          onClick={handleLogout}
          onMouseEnter={(e) => collapsed && handleMouseEnter(e, t('Logout'))}
          onMouseMove={(e) => collapsed && handleMouseMove(e)}
          onMouseLeave={() => collapsed && handleMouseLeave()}
          type="button"
          className={cn(
            'group relative w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold text-red-500 bg-red-500/10 hover:bg-red-500/20 transition-colors',
            collapsed && 'justify-center'
          )}
        >
          <MdLogout className="w-4 h-4" />
          {!collapsed && <span className="uppercase tracking-wide">{t('Logout')}</span>}
        </button>
      </div>

      {/* Tooltip for collapsed mode */}
      {collapsed && tooltip && (
        <div
          className={cn(
            'fixed px-3 py-1.5 bg-[hsl(var(--card))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] text-sm uppercase tracking-wide rounded-lg whitespace-nowrap shadow-lg z-[100] pointer-events-none transition-all duration-150 ease-in-out',
            showTooltip ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          )}
          style={{
            left: `${tooltip.x + 10}px`,
            top: `${tooltip.y + 10}px`,
          }}
        >
          {t(tooltip.label)}
        </div>
      )}
    </aside>
  );
}